/**
 * ResendEmailButton Component
 * Button with cooldown timer for resending email confirmation
 */

import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faClock } from '@fortawesome/free-solid-svg-icons';
import Button from '../../../../../shared/components/Button';

interface ResendEmailButtonProps {
  onResend: () => Promise<void>;
  canResend: boolean;
  cooldownSeconds: number;
  isLoading: boolean;
  attempts: number;
  maxAttempts: number;
}

export const ResendEmailButton: React.FC<ResendEmailButtonProps> = ({
  onResend,
  canResend,
  cooldownSeconds,
  isLoading,
  attempts,
  maxAttempts
}) => {
  const handleResend = async () => {
    try {
      await onResend();
    } catch (error) {
      console.error('Resend email error:', error);
    }
  };

  const formatCooldownTime = (seconds: number): string => {
    if (seconds >= 60) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    }
    return `${seconds}s`;
  };

  const getButtonText = (): string => {
    if (isLoading) {
      return 'Mengirim...';
    }
    
    if (!canResend && cooldownSeconds > 0) {
      return `Kirim Ulang dalam ${formatCooldownTime(cooldownSeconds)}`;
    }
    
    if (attempts >= maxAttempts) {
      return 'Batas Kirim Ulang Tercapai';
    }
    
    return attempts > 0 ? 'Kirim Ulang Email' : 'Kirim Ulang Email';
  };

  const isDisabled = !canResend || isLoading || attempts >= maxAttempts;

  return (
    <div className="space-y-3">
      <Button
        onClick={handleResend}
        disabled={isDisabled}
        loading={isLoading}
        variant="secondary"
        className="w-full"
      >
        <FontAwesomeIcon 
          icon={!canResend && cooldownSeconds > 0 ? faClock : faPaperPlane} 
          className="w-4 h-4 mr-2" 
        />
        {getButtonText()}
      </Button>
      
      {/* Compact Status Messages */}
      <div className="text-center">
        {!canResend && cooldownSeconds > 0 && (
          <p className="text-xs text-gray-500">
            Tunggu {formatCooldownTime(cooldownSeconds)} untuk kirim ulang
          </p>
        )}
        
        {attempts > 0 && attempts < maxAttempts && (
          <p className="text-xs text-gray-500">
            {attempts}/{maxAttempts} percobaan terpakai
          </p>
        )}
        
        {attempts >= maxAttempts && (
          <div className="p-2 bg-red-50 border border-red-200 rounded-xl">
            <p className="text-xs text-red-700">
              Batas tercapai. Hubungi support jika diperlukan.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};